"use client";

import React from "react";
import { Plus, ThermometerSun, Bath, ShieldCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
const hotspots = [{
  id: 1,
  top: "22%",
  left: "48%",
  title: "Temperature Controlled",
  description: "Climate-controlled environment ensures your pet is comfortable in any weather, year-round.",
  iconName: "thermometer"
}, {
  id: 2,
  top: "58%",
  left: "72%",
  title: "Hydro-Massage Tub",
  description: "Our clean, stainless steel tub and gentle shampoo mixes directly with the water and comes out of the hose. We keep the water warm to ensure the most relaxing spa experience for your pet.",
  iconName: "bath"
}, {
  id: 3,
  top: "42%",
  left: "36%",
  title: "Hospital Grade Sanitization",
  description: "Our professional blowers are enclosed in a soundproof encasement to ensure a relaxing and stress-free experience for your pet.",
  iconName: "shield"
}, {
  id: 4,
  top: "82%",
  left: "88%",
  title: "Audio & Lighting Control",
  description: "We are able to control the soothing music and adjust the lighting to ensure a stress-free grooming experience for your pet.",
  iconName: "plus"
}];
export default function BrandNewLuxeMobileSpa() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const getIcon = (name: string) => {
    switch (name) {
      case "thermometer":
        return <ThermometerSun className="size-5 text-primary" />;
      case "bath":
        return <Bath className="size-5 text-primary" />;
      case "shield":
        return <ShieldCheck className="size-5 text-primary" />;
      default:
        return <Plus className="size-5 text-primary" />;
    }
  };
  return <section id="brand-new-luxe-mobile-spa" className="bg-background relative overflow-hidden ![padding:60px_0px_60px_0px]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
            Get to Know Our <span className="font-serif italic text-primary">LUXE</span> Mobile Spa
          </h2>
          <p className="text-xl text-muted-foreground">
            Step inside the BarkBath mobile grooming unit. Fully equipped with state-of-the-art features to provide a stress-free, premium spa experience right at your doorstep.
          </p>
        </div>

        <div ref={imageRef} className={`relative max-w-5xl mx-auto transition-all duration-1000 delay-200 transform ${isImageVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
          {}
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-8 border-background bg-background">
            <img src="https://together-phoenix-1.10web.cloud/wp-content/uploads/2026/03/Groomer-Van-2-scaled.png" alt="BarkBath Luxe Mobile Spa Van" className="w-full h-auto object-cover aspect-[4/3] md:aspect-[21/9]" />

            {}
            {hotspots.map((hotspot, i) => <div key={hotspot.id} data-index={i} className="absolute z-20" style={{
            top: hotspot.top,
            left: hotspot.left,
            transform: 'translate(-50%, -50%)'
          }}>
                <Popover>
                  <PopoverTrigger asChild>
                    <div className="relative group cursor-pointer">
                      {}
                      <div className={`absolute inset-0 rounded-full bg-primary/40 animate-ping scale-150 duration-1000 ${i === 0 ? "translate-x-2" : ""}`}></div>
                      {}
                      <Button size="icon" className="relative rounded-full size-10 md:size-12 bg-primary text-primary-foreground shadow-lg border-2 border-background hover:bg-primary hover:scale-110 transition-transform" aria-label={`View details for ${hotspot.title}`}>
                        <Plus className="size-5 md:size-6" />
                      </Button>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-5 rounded-2xl shadow-xl border-border/50 bg-background/95 backdrop-blur-md" sideOffset={12}>
                    <div className="flex items-start gap-4">
                      <div className="p-2.5 bg-accent rounded-full shrink-0">
                        {getIcon(hotspot.iconName)}
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground mb-1.5">{hotspot.title}</h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {hotspot.description}
                        </p>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>)}
          </div>

          {}
          <div className="absolute -bottom-6 -right-4 md:-bottom-8 md:-right-8 bg-background p-3 md:p-4 rounded-full shadow-xl border-4 border-accent animate-bounce" style={{
          animationDuration: '3s'
        }}>
             <div className="bg-primary text-primary-foreground text-xs md:text-sm font-bold py-2 px-4 rounded-full uppercase tracking-wider whitespace-nowrap">
               Fully Equipped
             </div>
          </div>
        </div>
      </div>
    </section>;
}